// The plan in the person's words (02-DESIGN/17-plans/02-features-and-limits.md
// § the console; 01-the-entitlement.md § what identity.tenant.get answers,
// § its life; 02-DESIGN/03-console/01-the-console.md § Settings: the Plan
// tab; decision 0070 § 10): the state in one line, each feature *included*
// or *not in Acme's plan*, each limit with its use beside it and what to do
// over it, the refusals of *Apply a plan key* in one sentence each, every
// refusal a plan causes naming the plan, and Home's one line. Pure: the Plan
// tab, Home and the forms that check beforehand draw on it.
//
// The words: *plan*; *plan key* for what is applied on Settings, *license*
// for an install's and a solution's; *included* and *not in Acme's plan*;
// *allows* for a limit; *trial*. Never entitlement, registry, quota, addon
// or tier on a page.

import { bytes, plural } from "../../ui/components.tsx";
import {
  EntitlementCode,
  Feature,
  Limit,
  NO_BOUND,
  PlanCode,
  PlanState,
  REGISTRY,
  Source,
  type InstallLicense,
  type Tenant,
} from "../../wire/auth.ts";
import { RegistrationState, type Registration } from "../../wire/workloads.ts";

const DAY = 24 * 60 * 60 * 1000;

/** codeOf is a refusal's code, or nothing. */
function codeOf(err: unknown): string | undefined {
  return typeof err === "object" && err !== null && "code" in err && typeof err.code === "string"
    ? err.code
    : undefined;
}

/** messageOf is a refusal's message, or nothing. */
function messageOf(err: unknown): string {
  return typeof err === "object" && err !== null && "message" in err && typeof err.message === "string"
    ? err.message
    : "";
}

/** upper starts a sentence with a capital. */
function upper(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** dayOf renders an instant as a day: *1 March*, with the year where it is not this one's — *1 March 2027*. */
export function dayOf(iso: string | undefined, now: number = Date.now()): string {
  if (iso === undefined || iso === "") {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  const sameYear = d.getUTCFullYear() === new Date(now).getUTCFullYear();
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  }).format(d);
}

/** daysLeft is how many days remain until an instant, rounded up; zero once it has passed. */
export function daysLeft(iso: string | undefined, now: number = Date.now()): number {
  if (iso === undefined || iso === "") {
    return 0;
  }
  const ms = new Date(iso).getTime() - now;
  return Number.isNaN(ms) || ms <= 0 ? 0 : Math.ceil(ms / DAY);
}

/**
 * Holder is whose plan covers the workspace: its own on a root, its root's on
 * a child — *Northwind's plan covers this workspace* — and, on a child, who
 * narrowed a value in its blueprint: the workspace that manages it.
 */
export interface Holder {
  /** Whose plan it is, by name. */
  name: string;
  /** Whether the workspace is a child, covered by its root's plan: no *Apply* here. */
  child: boolean;
  /** Who sets a value the blueprint narrowed: the parent, by name. */
  setter: string;
}

/** holderOf is whose plan covers the workspace, from the reply's root and parent. */
export function holderOf(t: Tenant, own: string): Holder {
  const named = (p: { slug: string; name?: string } | undefined) =>
    p === undefined ? undefined : p.name !== undefined && p.name !== "" ? p.name : p.slug;
  const root = named(t.root);
  return {
    name: root ?? own,
    child: root !== undefined,
    setter: named(t.parent) ?? root ?? own,
  };
}

/**
 * planLine is the plan's name, the licensee and the state in one line:
 * *Acme is on Business, valid until 1 March 2027*; *Trial — 12 days left*;
 * *Acme's plan expired on 1 March; it keeps working until 15 March*; *Acme's
 * plan has lapsed; apply a plan key to continue*. A reply that answers no
 * plan says the plan's name as the platform set it.
 */
export function planLine(t: Tenant, own: string, now: number = Date.now()): string {
  const h = holderOf(t, own);
  const e = t.entitlement;
  if (e === undefined) {
    return `${h.name} is on ${t.plan}`;
  }
  const who = e.licensee !== undefined && e.licensee !== "" && !h.child ? e.licensee : h.name;
  switch (e.state) {
    case PlanState.trial: {
      const n = daysLeft(e.ends_at, now);
      return n === 0 ? "Trial — it ends today" : `Trial — ${plural(n, "day")} left`;
    }
    case PlanState.grace:
      return `${h.name}'s plan expired on ${dayOf(e.expires, now)}; it keeps working until ${dayOf(e.ends_at, now)}`;
    case PlanState.lapsed:
      return h.child
        ? `${h.name}'s plan has lapsed; an admin of ${h.name} applies a plan key to continue`
        : `${h.name}'s plan has lapsed; apply a plan key to continue`;
    default:
      return e.ends_at !== undefined && e.ends_at !== ""
        ? `${who} is on ${e.plan}, valid until ${dayOf(e.ends_at, now)}`
        : `${who} is on ${e.plan}`;
  }
}

/**
 * licenseLine is a self-hosted install's license in one line, the same way:
 * *This install is licensed to Acme until 1 March 2027*; past its expiry, in
 * grace and lapsed. Nothing on the managed environment, which answers none.
 */
export function licenseLine(l: InstallLicense | undefined, now: number = Date.now()): string | undefined {
  if (l === undefined) {
    return undefined;
  }
  const to = l.licensee !== undefined && l.licensee !== "" ? ` to ${l.licensee}` : "";
  switch (l.state) {
    case PlanState.grace:
      return `This install's license expired on ${dayOf(l.expires, now)}; new workspaces can be created until ${dayOf(l.ends_at, now)}`;
    case PlanState.lapsed:
      return LICENSE_EXPIRED;
    default:
      return l.ends_at !== undefined && l.ends_at !== ""
        ? `This install is licensed${to} until ${dayOf(l.ends_at, now)}`
        : `This install is licensed${to}`;
  }
}

/** LICENSE_EXPIRED is what every admin reads in a self-hosted install past its license's grace. */
export const LICENSE_EXPIRED = "This install's license has expired; new workspaces cannot be created";

/** FEATURE_WORDS are the registry's features in the words people read. */
export const FEATURE_WORDS: Readonly<Record<string, string>> = {
  [Feature.ownProvider]: "your own sign-in",
  [Feature.manageWorkspaces]: "managing workspaces",
  [Feature.publishInstall]: "publishing solutions to every workspace",
  [Feature.hostCapabilities]: "accelerator hosts",
};

/** LIMIT_WORDS are the registry's limits in the words people read. */
export const LIMIT_WORDS: Readonly<Record<string, string>> = {
  [Limit.storage]: "Storage",
  [Limit.workspaces]: "Workspaces beneath it",
  [Limit.invites]: "Invites a day",
  [Limit.workloads]: "Workloads",
  [Limit.vcpus]: "Virtual CPUs",
  [Limit.memory]: "Memory",
};

/**
 * sourceWords says where a value comes from, on a child only: *set by
 * Northwind* where the blueprint narrowed it, *in Northwind's plan*
 * otherwise. A root's values are its own plan's and say nothing more.
 */
export function sourceWords(source: string | undefined, h: Holder): string | undefined {
  if (!h.child) {
    return undefined;
  }
  return source === Source.blueprint ? `set by ${h.setter}` : `in ${h.name}'s plan`;
}

/** FeatureRow is one feature as the tab lists it. */
export interface FeatureRow {
  name: string;
  words: string;
  included: boolean;
  /** *included*, or *not in Acme's plan*. */
  state: string;
  source?: string;
}

/** featureRows is each feature of the registry, included or not, in the design's order. */
export function featureRows(t: Tenant, own: string): FeatureRow[] {
  const h = holderOf(t, own);
  return REGISTRY.filter((e) => e.kind === "feature").map((e) => {
    const g = t.entitlement?.features[e.name];
    const included = g?.granted === true;
    return {
      name: e.name,
      words: upper(FEATURE_WORDS[e.name] ?? e.name),
      included,
      state: included ? "included" : `not in ${h.name}'s plan`,
      source: sourceWords(g?.source, h),
    };
  });
}

/**
 * Uses are what the page can read of each limit's use: storage reserved by
 * the stores, workspaces beneath, and the workloads, virtual CPUs and memory
 * the registrations that count hold. A use the page cannot read is absent.
 */
export type Uses = Partial<Record<string, number>>;

/** Compute is what the workload service counts: registrations, virtual CPUs and memory in bytes. */
export interface Compute {
  workloads: number;
  vcpus: number;
  memory: number;
}

/**
 * computeOf counts as the workload service does (02-features-and-limits.md §
 * per workspace, across the tree): every registration not retired —
 * suspended included — the workspace's own and every solution's, a platform
 * registration never; virtual CPUs and memory times its copies.
 */
export function computeOf(
  regs: readonly Pick<Registration, "platform" | "state" | "resources" | "replicas">[],
): Compute {
  const out: Compute = { workloads: 0, vcpus: 0, memory: 0 };
  for (const r of regs) {
    if ((r.platform !== undefined && r.platform !== "") || r.state === RegistrationState.retired) {
      continue;
    }
    const copies = r.replicas !== undefined && r.replicas > 0 ? r.replicas : 1;
    out.workloads += 1;
    out.vcpus += r.resources.cpus * copies;
    out.memory += r.resources.memory_mib * 1024 * 1024 * copies;
  }
  return out;
}

/** amount renders a limit's number in its unit: bytes, a count a day, or a count. */
export function amount(name: string, n: number): string {
  switch (name) {
    case Limit.storage:
    case Limit.memory:
      return bytes(n);
    case Limit.invites:
      return `${String(n)} a day`;
    default:
      return String(n);
  }
}

/** LimitRow is one limit as the tab lists it. */
export interface LimitRow {
  name: string;
  words: string;
  /** What the plan allows: the amount, or *no limit*. */
  allows: string;
  /** The use beside it, where the page reads one: *3 of 10*. */
  use?: string;
  /** Over its limit: what to do, in one sentence. */
  over?: string;
  source?: string;
}

/** OVER_ACTION is what an admin does about a value over its limit. */
const OVER_ACTION: Readonly<Record<string, string>> = {
  [Limit.storage]: "remove a store",
  [Limit.workspaces]: "remove a workspace beneath it",
  [Limit.workloads]: "remove a workload",
  [Limit.vcpus]: "remove a workload or give one fewer copies",
  [Limit.memory]: "remove a workload or give one less memory",
};

/** limitRows is each limit of the registry with its use beside it, in the design's order. */
export function limitRows(t: Tenant, own: string, uses: Uses): LimitRow[] {
  const h = holderOf(t, own);
  const more = h.child ? `ask ${h.name} for more room` : "apply a plan with more room";
  return REGISTRY.filter((e) => e.kind === "limit").map((e) => {
    const v = t.entitlement?.limits[e.name];
    const bounded = v !== undefined && v.value !== NO_BOUND && v.value >= 0;
    const used = uses[e.name];
    const row: LimitRow = {
      name: e.name,
      words: LIMIT_WORDS[e.name] ?? e.name,
      allows: v === undefined ? "not answered" : bounded ? amount(e.name, v.value) : "no limit",
      source: sourceWords(v?.source, h),
    };
    if (used !== undefined) {
      row.use = bounded
        ? `${amount(e.name, used)} of ${amount(e.name, v.value)}`
        : `${amount(e.name, used)} in use`;
      if (bounded && used > v.value) {
        const action = OVER_ACTION[e.name];
        row.over = `Over its limit: ${action !== undefined ? `${action}, or ` : ""}${more}.`;
      }
    }
    return row;
  });
}

/**
 * keyRefusal is a refused *Apply a plan key* in one sentence, or nothing for
 * a refusal that is not the key's.
 */
export function keyRefusal(err: unknown): string | undefined {
  switch (codeOf(err)) {
    case EntitlementCode.invalid:
      return "This key is not signed by Impire.";
    case EntitlementCode.wrongInstall:
      return "This key is for another install.";
    case EntitlementCode.wrongWorkspace:
      return "This key is for another workspace.";
    case EntitlementCode.expired:
      return "This key has expired.";
    case EntitlementCode.stale:
      return "This key is older than the one applied.";
    case PlanCode.notARoot:
      return "Only the workspace at the top of the tree can apply a plan key.";
  }
  return undefined;
}

/** isPlanRefusal reports whether a refusal is one a plan caused. */
export function isPlanRefusal(err: unknown): boolean {
  const c = codeOf(err);
  return (
    c === PlanCode.notInPlan ||
    c === PlanCode.licenseExpired ||
    (c === PlanCode.limit && limitOf(err) !== undefined)
  );
}

/** The limits a `limit` refusal names, as the workload service writes them first in its message. */
const LIMIT_PREFIX = /^(workloads|vcpus|memory):/;

/**
 * limitOf is which limit a `limit` refusal is about: the workload service
 * names it first in its message; the auth service's workspaces refusal says
 * *may hold N workspaces beneath it*.
 */
function limitOf(err: unknown): string | undefined {
  const m = messageOf(err);
  const p = LIMIT_PREFIX.exec(m);
  if (p !== null) {
    return p[1];
  }
  return /workspaces beneath/.test(m) ? Limit.workspaces : undefined;
}

/** numbers are the figures a `limit` refusal names: what the plan allows and what the request would make. */
function numbers(err: unknown): { allows?: number; would?: number } {
  const m = messageOf(err);
  const allows = /allows (\d+)/.exec(m) ?? /may hold (\d+)/.exec(m);
  const would = /would make (\d+)/.exec(m);
  return {
    allows: allows?.[1] !== undefined ? Number(allows[1]) : undefined,
    would: would?.[1] !== undefined ? Number(would[1]) : undefined,
  };
}

/** featureRefusal is a feature not in the plan, in the words the Sign-in tab already uses. */
export function featureRefusal(feature: string | undefined, holder: string, own: string): string {
  switch (feature) {
    case Feature.ownProvider:
      return `${own}'s own sign-in is not in ${holder}'s plan.`;
    case Feature.manageWorkspaces:
      return `Managing workspaces is not in ${holder}'s plan.`;
    case Feature.publishInstall:
      return `Publishing solutions to every workspace is not in ${holder}'s plan.`;
    case Feature.hostCapabilities:
      return `Accelerator hosts are not in ${holder}'s plan.`;
  }
  return `This is not in ${holder}'s plan.`;
}

/** limitSentence is a limit the request would pass, in the design's words. */
export function limitSentence(name: string, holder: string, allows: number, would: number): string {
  switch (name) {
    case Limit.workloads:
      return `${holder}'s plan allows ${plural(allows, "workload")} and ${String(Math.max(would - 1, 0))} exist.`;
    case Limit.vcpus:
      return `${holder}'s plan allows ${plural(allows, "virtual CPU")}; this workload would make ${String(would)}.`;
    case Limit.memory:
      return `${holder}'s plan allows ${bytes(allows)} of memory; this workload would make ${bytes(would)}.`;
    case Limit.workspaces:
      return `${holder}'s plan allows ${plural(allows, "workspace")} beneath it.`;
  }
  return `${holder}'s plan allows ${String(allows)}; this would make ${String(would)}.`;
}

/**
 * planRefusal is a refusal a plan caused, naming the plan, or nothing for any
 * other refusal: *Acme's plan allows 10 workloads and 10 exist*, *accelerator
 * hosts are not in Acme's plan*. `feature` is the one the form is about,
 * where it knows; a workload's figures are read from the service's message.
 */
export function planRefusal(err: unknown, holder: string, own: string, feature?: string): string | undefined {
  switch (codeOf(err)) {
    case PlanCode.notInPlan: {
      const f =
        feature ?? (/host capabilit|requires/i.test(messageOf(err)) ? Feature.hostCapabilities : undefined);
      return featureRefusal(f, holder, own);
    }
    case PlanCode.licenseExpired:
      return `${LICENSE_EXPIRED}.`;
    case PlanCode.limit: {
      const name = limitOf(err);
      if (name === undefined) {
        return undefined;
      }
      const n = numbers(err);
      if (n.allows === undefined) {
        return undefined;
      }
      if (name === Limit.memory) {
        // The service names memory in bytes, with MiB beside.
        return limitSentence(name, holder, n.allows, n.would ?? n.allows);
      }
      return limitSentence(name, holder, n.allows, n.would ?? n.allows + 1);
    }
  }
  return undefined;
}

/**
 * computeCheck is what the *Add a workload* form says before it is submitted:
 * the room left in the plan for each bounded limit, and the first limit the
 * workload would pass, in the refusal's words. Nothing where nothing bounds.
 */
export function computeCheck(
  t: Tenant | undefined,
  holder: string,
  used: Compute | undefined,
  adding: { cpus: number; memoryMiB: number; copies: number } | undefined,
): { room?: string; refusal?: string } {
  const limits = t?.entitlement?.limits;
  if (limits === undefined || used === undefined) {
    return {};
  }
  const bound = (name: string) => {
    const v = limits[name];
    return v !== undefined && v.value !== NO_BOUND && v.value >= 0 ? v.value : undefined;
  };
  const w = bound(Limit.workloads);
  const c = bound(Limit.vcpus);
  const m = bound(Limit.memory);
  const parts: string[] = [];
  if (w !== undefined) {
    parts.push(plural(Math.max(w - used.workloads, 0), "workload"));
  }
  if (c !== undefined) {
    parts.push(plural(Math.max(c - used.vcpus, 0), "virtual CPU"));
  }
  if (m !== undefined) {
    parts.push(`${bytes(Math.max(m - used.memory, 0))} of memory`);
  }
  const out: { room?: string; refusal?: string } = {};
  if (parts.length > 0) {
    out.room = `Room left in ${holder}'s plan: ${parts.join(", ")}.`;
  }
  if (adding !== undefined) {
    const vcpus = used.vcpus + adding.cpus * adding.copies;
    const memory = used.memory + adding.memoryMiB * 1024 * 1024 * adding.copies;
    if (w !== undefined && used.workloads + 1 > w) {
      out.refusal = limitSentence(Limit.workloads, holder, w, used.workloads + 1);
    } else if (c !== undefined && vcpus > c) {
      out.refusal = limitSentence(Limit.vcpus, holder, c, vcpus);
    } else if (m !== undefined && memory > m) {
      out.refusal = limitSentence(Limit.memory, holder, m, memory);
    }
  }
  return out;
}

/**
 * homeLine is Home's one line for a root's admins while the plan needs them —
 * *Acme's trial ends in 12 days*, *Acme's plan expired on 1 March — apply a
 * plan key by 15 March* — and, in a self-hosted install past its license's
 * grace, the install's line to every admin. Nothing while nothing is needed.
 */
export function homeLine(t: Tenant, own: string, now: number = Date.now()): string | undefined {
  if (t.license?.state === PlanState.lapsed) {
    return LICENSE_EXPIRED;
  }
  const e = t.entitlement;
  if (e === undefined || t.root !== undefined) {
    return undefined;
  }
  switch (e.state) {
    case PlanState.trial: {
      const n = daysLeft(e.ends_at, now);
      return n === 0 ? `${own}'s trial ends today` : `${own}'s trial ends in ${plural(n, "day")}`;
    }
    case PlanState.grace:
      return `${own}'s plan expired on ${dayOf(e.expires, now)} — apply a plan key by ${dayOf(e.ends_at, now)}`;
    case PlanState.lapsed:
      return `${own}'s plan has lapsed — apply a plan key to continue`;
  }
  return undefined;
}

/**
 * fromPlan reports whether a value the workspace carries is its plan's — the
 * root's plan key, the install's table or its default — and not a narrowing
 * of its parent's blueprint, so that a refusal on it names the plan.
 */
export function fromPlan(t: Tenant | undefined, name: string): boolean {
  const e = t?.entitlement;
  if (e === undefined) {
    return false;
  }
  const source = e.features[name]?.source ?? e.limits[name]?.source;
  return source !== undefined && source !== Source.blueprint;
}
